import { useState, useEffect, useRef } from "react";
import { FormContainer, TextareaStyled, ButtonGroup, ButtonStyled } from "./styles";
import comentarioService, { ICreateResposta } from "../../../../services/comentarioService";
import toast from "react-hot-toast";
import { toastDefsError, toastDefs } from "../../../../config/toastConfig";
import { useTheme } from "styled-components";
import { BadgeTipoDestinatario } from "../CardComentario/styles";
import { getTipoLabelDestinatario } from "../../../../utility/comentarioTipos";

interface FormRespostaProps {
    id_comentario_pai: number;
    id_proposta: number;
    onRespostaCriada: () => void;
    onCancelar: () => void;
    destinatarioNome?: string | null;
    destinatarioTipo?: 'Operador' | 'Solicitante' | 'Profissional' | 'OperadoresInterno' | null;
    userTipo?: string;
}

export default function FormResposta({ 
    id_comentario_pai, 
    id_proposta, 
    onRespostaCriada, 
    onCancelar,
    destinatarioNome,
    destinatarioTipo,
    userTipo
}: FormRespostaProps) {
    const theme = useTheme() as any;
    const [texto, setTexto] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [erroTexto, setErroTexto] = useState("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.focus();
            autoResize(textareaRef.current);
        }

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const autoResize = (textarea: HTMLTextAreaElement) => {
        textarea.style.height = 'auto';
        const maxHeight = 300;
        const newHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${newHeight}px`;
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const novoTexto = e.target.value;
        setTexto(novoTexto);
        autoResize(e.target);
        
        if (erroTexto && novoTexto.trim()) {
            setErroTexto("");
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && e.ctrlKey && texto.trim()) {
            e.preventDefault();
            handleResponder();
        }
    };

    const handleResponder = async () => {
        if (isLoading) return;
        
        if (!texto.trim()) {
            setErroTexto("A resposta não pode estar vazia");
            return;
        }
        
        setErroTexto("");

        setIsLoading(true);
        try {
            const data: ICreateResposta = {
                id_proposta,
                id_comentario_pai,
                texto: texto.trim()
            };

            await comentarioService.createResposta(data);
            
            if (!isMountedRef.current) return;
            
            toast.success("Resposta criada com sucesso!", toastDefs);
            setTexto("");
            onRespostaCriada();
        } catch (error: any) {
            if (!isMountedRef.current) return;
            
            console.error('Erro ao criar resposta:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao criar resposta';
            toast.error(errorMessage, toastDefsError);
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    return (
        <FormContainer data-form-resposta={id_comentario_pai}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <h3 style={{ fontSize: '14px', fontWeight: 500, color: 'var(--c6b7280)', margin: 0 }}>
                        Responder ao comentário
                    </h3>
                </div>
                {destinatarioTipo && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <label style={{ 
                            fontSize: '12px', 
                            color: theme?.colors?.interfaceText300 || '#9CA3AF', 
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                        }}>
                            Para:
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {
                                // Se o usuário for Solicitante ou Correspondente, não mostrar o nome, apenas a tag "Operador"
                                (userTipo === 'Solicitante' || userTipo === 'Profissional' || userTipo === 'Correspondente') && destinatarioTipo === 'Operador'
                                    ? null
                                    : destinatarioNome ? <span>{destinatarioNome}</span> : null
                            }
                            <BadgeTipoDestinatario>
                                {getTipoLabelDestinatario(destinatarioTipo)}
                            </BadgeTipoDestinatario>
                        </div>
                    </div>
                )}
                <div style={{ position: 'relative', width: '100%' }}>
                    <TextareaStyled
                        ref={textareaRef}
                        placeholder="Escreva sua resposta..."
                        value={texto}
                        onChange={handleTextChange}
                        onKeyDown={handleKeyDown}
                        rows={3}
                        aria-label="Campo de texto da resposta"
                        aria-invalid={!!erroTexto}
                        aria-describedby={erroTexto ? "erro-resposta" : undefined}
                        style={{
                            borderColor: erroTexto ? 'var(--cef4444)' : undefined
                        }}
                    />
                    {erroTexto && (
                        <div id="erro-resposta" style={{ padding: '4px 8px', color: 'var(--cef4444)', fontSize: '12px', marginTop: '4px' }} role="alert">
                            {erroTexto}
                        </div>
                    )}
                </div>

                <ButtonGroup>
                    <ButtonStyled 
                        variant="outline" 
                        onClick={onCancelar} 
                        disabled={isLoading}
                        aria-label="Cancelar resposta"
                    >
                        Cancelar
                    </ButtonStyled>
                    <ButtonStyled 
                        onClick={handleResponder} 
                        disabled={!texto.trim() || isLoading}
                        aria-label="Enviar resposta"
                    >
                        {isLoading ? 'Respondendo...' : 'Responder'}
                    </ButtonStyled>
                </ButtonGroup>
        </FormContainer>
    );
}

